import * as React from "react";
import { useState, useEffect } from "react";
import { api } from "../api";
import { useParams } from "react-router";
import { RLink } from "../DesignSystem/RLink";
import { TwoColumnView, Page } from "../DesignSystem/formatting/TwoColumnView";
import { FeedPanel } from "../DesignSystem/formatting/FeedComponents";
import { ChipsAPI } from "../../../src/apidefs";
import { MusicalNoteIcon } from "@heroicons/react/24/solid";

type PlaysResponse = Exclude<
  ChipsAPI["/api/user/:username/plays"]["GET"]["response"],
  { type: "error" | "success" }
>;

type StatsResponse = Exclude<
  ChipsAPI["/api/user/:username/plays/stats"]["GET"]["response"],
  { type: "error" | "success" }
>;

const StatsPanel = ({ stats }: { stats: StatsResponse | null }) => {
  if (!stats) {
    return (
      <FeedPanel title={<>Listening Statistics</>}>
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </FeedPanel>
    );
  }

  const userCreatedDate = new Date(stats.userCreated);
  const now = new Date();
  const daysSinceSignup = Math.max(
    1,
    Math.floor((now.getTime() - userCreatedDate.getTime()) / (1000 * 60 * 60 * 24))
  );
  const avgSongsPerDay = (stats.totalListens / daysSinceSignup).toFixed(2);

  const statItems = [
    { label: "Total Listens", value: stats.totalListens.toLocaleString() },
    { label: "Unique Songs", value: stats.uniqueSongs.toLocaleString() },
    { label: "Avg. Listens per Day", value: avgSongsPerDay },
  ];

  return (
    <>
      <FeedPanel title={<>Listening Statistics</>}>
        <div className="space-y-3">
          {statItems.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-slate-500 dark:text-slate-400">
                {stat.label}
              </span>
              <span className="font-medium">{stat.value}</span>
            </div>
          ))}
        </div>
      </FeedPanel>

      {stats.topAuthors.length > 0 && (
        <FeedPanel title={<>Top Artists</>}>
          <div className="space-y-3">
            {stats.topAuthors.map((author, index) => (
              <div key={author.userId} className="flex items-center gap-3">
                <span className="w-5 text-sm font-medium text-slate-400 dark:text-slate-500">
                  {index + 1}.
                </span>
                <img
                  src={author.avatarUrl}
                  alt={author.username}
                  className="h-8 w-8 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <RLink
                    to={`/user/${author.username}`}
                    className="block truncate font-medium hover:underline"
                  >
                    {author.username}
                  </RLink>
                </div>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {author.playCount.toLocaleString()} plays
                </span>
              </div>
            ))}
          </div>
        </FeedPanel>
      )}
    </>
  );
};

export function AllPlaysPage() {
  const { username } = useParams<{ username: string }>();
  const [plays, setPlays] = useState<PlaysResponse>([]);
  const [stats, setStats] = useState<StatsResponse | null>(null);

  useEffect(() => {
    async function fetchData() {
      const [playsResult, statsResult] = await Promise.all([
        api.get<"/api/user/:username/plays">(`/api/user/${username}/plays`),
        api.get<"/api/user/:username/plays/stats">(
          `/api/user/${username}/plays/stats`
        ),
      ]);

      if (!api.isToast(playsResult)) {
        setPlays(playsResult);
      }

      if (!api.isToast(statsResult)) {
        setStats(statsResult);
      }
    }

    fetchData();
  }, [username]);

  return (
    <Page>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          {username}'s plays
        </h1>
        <RLink
          to={`/user/${username}`}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Return to {username}'s page
        </RLink>
      </div>

      <TwoColumnView
        left={
          <div className="space-y-3">
            {plays.map(({ entry_id, title, count }) => (
              <RLink
                key={entry_id}
                to={`/entry/${entry_id}`}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-all hover:border-slate-300 hover:shadow-md dark:border-white/10 dark:bg-slate-800 dark:hover:border-white/20"
              >
                <MusicalNoteIcon className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                <span className="min-w-0 flex-1 truncate font-medium">
                  {title}
                </span>
                <span className="flex-shrink-0 text-sm text-slate-500 dark:text-slate-400">
                  {count.toLocaleString()} {count === 1 ? "play" : "plays"}
                </span>
              </RLink>
            ))}
            {plays.length === 0 && (
              <div className="py-8 text-center text-slate-500 dark:text-slate-400">
                No plays yet.
              </div>
            )}
          </div>
        }
        right={<StatsPanel stats={stats} />}
      />
    </Page>
  );
}
